import { Box, Button, Chip, Stack, type Theme, Typography, useMediaQuery } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import type { SWRResponse } from 'swr';
import Alert from '/@/components/bayer/alert';
import ModalV2 from '/@/components/bayer/modal-v2';
import Markdown from '/@/components/markdown/index.tsx';
import {
    type RVAdvisorInvitationApi,
    useAcceptAdvisorRvInvitation,
    useAcceptAllAdvisorRvInvitations,
    useDeclineAdvisorRvInvitation,
    useDeclineAllAdvisorRvInvitations,
} from '/@/hooks/nodes';
import { useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { PaginatedResponse } from '/@/types/core';
import { modalProps } from '.';
import AdvisorLinkIcon from './assets/advisor-link.svg';
import RVLinkIcon from './assets/rv-link.svg';
import {
    AdvisorDistributorNamesContext,
    AdvisorDistributorNamesProvider,
} from './pending-invites/advisor-invitation-list-common';
import MultipleRVInvitations from './pending-invites/multiple-rv-invitations.tsx';
import ReceivedInvitations from './pending-invites/received-invitations.tsx';

type LocalInvitationStatus = 'pending' | 'accepted' | 'declined';
type LocalInvitationState = Record<number, LocalInvitationStatus>;

const InvitationStatusChip = ({ status }: { status: LocalInvitationStatus }) => {
    if (status === 'accepted') {
        return <Chip label={_('Aceptada')} color='success' />;
    }
    if (status === 'declined') {
        return <Chip label={_('Rechazada')} color='error' />;
    }
    return null;
};

const SingleInvitation = ({
    invitation,
    localStatus,
}: {
    invitation: RVAdvisorInvitationApi;
    localStatus: LocalInvitationStatus;
}) => {
    const advisorDistributorNames = useContext(AdvisorDistributorNamesContext);
    const distributorName = advisorDistributorNames[invitation.advisor.id] ?? '';
    const isRV = useIsValidatedRV();

    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    return (
        <Stack direction='column' spacing={3} alignItems='center'>
            <Box
                component='img'
                src={isRV ? AdvisorLinkIcon : RVLinkIcon}
                height={isSmOrLower ? '80px' : '160px'}
                width={isSmOrLower ? '80px' : '160px'}
            />
            <Stack className='mp-mask' direction='column'>
                <Typography
                    color='primary'
                    variant={isSmOrLower ? 'subtitle2' : 'h5'}
                    fontWeight={isSmOrLower ? 'bold' : 'normal'}
                >
                    {isRV ? invitation.advisor.name : invitation.rv.name}
                </Typography>
                <Typography color='primary' variant={isSmOrLower ? 'caption' : 'h5'} fontWeight='normal'>
                    {invitation.invitedBy === 'rv' ? _('RV Premium') : distributorName}
                </Typography>
            </Stack>
            <Typography
                color='primary'
                variant={isSmOrLower ? 'subtitle2' : 'h5'}
                fontWeight={isSmOrLower ? 'bold' : 'normal'}
            >
                {_('Te envió una solicitud de vinculación')}
            </Typography>
            <Typography color='primary' variant={isSmOrLower ? 'caption' : 'body1'}>
                {_(
                    'Si aceptas, vos y {{advisorName}} van a compartir la información de los productores Premium que tienen en común. Si rechazas, no se va a compartir información.',
                    { advisorName: isRV ? invitation.advisor.name : invitation.rv.name }
                )}
            </Typography>
            {localStatus !== 'pending' && <InvitationStatusChip status={localStatus} />}
        </Stack>
    );
};

const MultipleInvitations = ({
    invitations,
    onInviteMoreAdvisors,
    selectedInvitation,
    setSelectedInvitation,
    localStatusMap,
    onAcceptInvitation,
    onDeclineInvitation,
    processingInvitationId,
}: {
    invitations: RVAdvisorInvitationApi[];
    onInviteMoreAdvisors: () => void;
    selectedInvitation: RVAdvisorInvitationApi | null;
    setSelectedInvitation: (invitation: RVAdvisorInvitationApi) => void;
    localStatusMap: LocalInvitationState;
    onAcceptInvitation: (id: number) => Promise<void>;
    onDeclineInvitation: (id: number) => Promise<void>;
    processingInvitationId: number | null;
}) => {
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const isRV = useIsValidatedRV();

    return (
        <Stack direction='column' spacing={3} alignItems='center'>
            <Typography
                color='primary'
                variant={isSmOrLower ? 'subtitle1' : 'h5'}
                fontWeight={isSmOrLower ? 'bold' : 'normal'}
            >
                {_('Recibiste {{invitationsCount}} solicitudes de vinculación', {
                    invitationsCount: invitations.length,
                })}
            </Typography>
            {!isRV && (
                <Alert severity='blue' sx={{ width: '100%', textAlign: 'start' }}>
                    <Markdown>{`**${_('Importante:')}** ${_('Recordá que solo podes elegir un RV.')}`}</Markdown>
                </Alert>
            )}
            <Typography color='primary' variant={isSmOrLower ? 'caption' : 'body1'} textAlign='left'>
                {isRV
                    ? _(
                          'Si aceptas, vos y la persona que te envió la solicitud van a compartir la información de los productores Premium que tienen en común. Si rechazas, no se va a compartir información.'
                      )
                    : _(
                          'Seleccioná al RV con quien trabajás entre las siguientes opciones. Si aceptas la solicitud, ambos van a ver y trabajar con la información de los productores Premium que comparten.'
                      )}
            </Typography>
            {isRV ? (
                <ReceivedInvitations
                    invitations={invitations}
                    loading={false}
                    onInviteMoreAdvisors={onInviteMoreAdvisors}
                    localStatusMap={localStatusMap}
                    onAcceptInvitation={onAcceptInvitation}
                    onDeclineInvitation={onDeclineInvitation}
                    processingInvitationId={processingInvitationId}
                />
            ) : (
                <MultipleRVInvitations
                    invitations={invitations}
                    loading={false}
                    selectedInvitation={selectedInvitation}
                    onSelectInvitation={(invitation) => {
                        setSelectedInvitation(invitation);
                    }}
                    localStatusMap={localStatusMap}
                />
            )}
        </Stack>
    );
};

const RVOnboardingModalNewInvitationsReceived = ({
    pendingInvitationsQuery,
    open,
    onClose,
    onInviteMoreAdvisors,
}: {
    pendingInvitationsQuery: SWRResponse<PaginatedResponse<RVAdvisorInvitationApi>>;
    open: boolean;
    onClose: () => void;
    onInviteMoreAdvisors: () => void;
}) => {
    const isRV = useIsValidatedRV();
    const [selectedInvitation, setSelectedInvitation] = useState<RVAdvisorInvitationApi | null>(null);
    const acceptInvitationMutation = useAcceptAdvisorRvInvitation();
    const declineInvitationMutation = useDeclineAdvisorRvInvitation();
    const declineAllMutation = useDeclineAllAdvisorRvInvitations();
    const acceptAllMutation = useAcceptAllAdvisorRvInvitations();

    // Capture invitations when modal opens - don't update from SWR after that
    const [snapshotInvitations, setSnapshotInvitations] = useState<RVAdvisorInvitationApi[]>([]);
    const [localStatusMap, setLocalStatusMap] = useState<LocalInvitationState>({});
    const [processingInvitationId, setProcessingInvitationId] = useState<number | null>(null);
    const hasInitialized = useRef(false);

    // Initialize snapshot when modal opens with pending invitations
    useEffect(() => {
        if (open && pendingInvitationsQuery.data?.results && !hasInitialized.current) {
            const invitations = pendingInvitationsQuery.data.results;
            setSnapshotInvitations(invitations);
            // Initialize all as pending
            const initialStatus: LocalInvitationState = {};
            for (const inv of invitations) {
                initialStatus[inv.id] = 'pending';
            }
            setLocalStatusMap(initialStatus);
            hasInitialized.current = true;
        }
    }, [open, pendingInvitationsQuery.data?.results]);

    // Reset when modal closes
    useEffect(() => {
        if (!open) {
            hasInitialized.current = false;
            setSnapshotInvitations([]);
            setLocalStatusMap({});
            setSelectedInvitation(null);
            setProcessingInvitationId(null);
        }
    }, [open]);

    const updateInvitationStatus = (id: number, status: LocalInvitationStatus) => {
        setLocalStatusMap((prev) => ({ ...prev, [id]: status }));
    };

    const handleAcceptInvitation = async (id: number) => {
        setProcessingInvitationId(id);
        try {
            await acceptInvitationMutation.trigger(id);
            // For single invitations, close the modal directly
            if (snapshotInvitations.length === 1) {
                onClose();
            } else {
                updateInvitationStatus(id, 'accepted');
            }
        } finally {
            setProcessingInvitationId(null);
        }
    };

    const handleDeclineInvitation = async (id: number) => {
        setProcessingInvitationId(id);
        try {
            await declineInvitationMutation.trigger(id);
            // For single invitations, close the modal directly
            if (snapshotInvitations.length === 1) {
                onClose();
            } else {
                updateInvitationStatus(id, 'declined');
            }
        } finally {
            setProcessingInvitationId(null);
        }
    };

    const markAllAsAccepted = () => {
        setLocalStatusMap((prev) => {
            const updated = { ...prev };
            for (const id of Object.keys(updated)) {
                if (updated[Number(id)] === 'pending') {
                    updated[Number(id)] = 'accepted';
                }
            }
            return updated;
        });
    };

    const markAllAsDeclined = () => {
        setLocalStatusMap((prev) => {
            const updated = { ...prev };
            for (const id of Object.keys(updated)) {
                if (updated[Number(id)] === 'pending') {
                    updated[Number(id)] = 'declined';
                }
            }
            return updated;
        });
    };

    const handleDeclineAllAndNavigate = async () => {
        await declineAllMutation.trigger();
        markAllAsDeclined();
        onInviteMoreAdvisors();
    };

    const handleAcceptSelected = async () => {
        if (selectedInvitation) {
            await acceptInvitationMutation.trigger(selectedInvitation.id);
            updateInvitationStatus(selectedInvitation.id, 'accepted');
        }
    };

    const handleAcceptAll = async () => {
        await acceptAllMutation.trigger();
        markAllAsAccepted();
    };

    // Use snapshot invitations for rendering
    const invitations = snapshotInvitations;
    const pendingCount = Object.values(localStatusMap).filter((s) => s === 'pending').length;
    const hasPendingInvitations = pendingCount > 0;

    const singleInvitation = invitations.length === 1 ? invitations[0] : null;
    const singleStatus = singleInvitation ? localStatusMap[singleInvitation.id] || 'pending' : null;

    // Only check live query conditions if we haven't initialized the snapshot yet
    // Once initialized, keep showing the modal with snapshot data
    const hasSnapshot = snapshotInvitations.length > 0;
    if (!hasSnapshot) {
        if (!pendingInvitationsQuery.data?.count) return null;
        if (isRV && pendingInvitationsQuery.data?.results?.[0]?.invitedBy === 'rv') return null;
        if (!isRV && pendingInvitationsQuery.data?.results?.[0]?.invitedBy === 'advisor') return null;
    }

    return (
        <AdvisorDistributorNamesProvider>
            <ModalV2
                open={open}
                onClose={onClose}
                {...modalProps}
                title={singleInvitation ? _('Nueva solicitud') : undefined}
                actions={
                    singleInvitation && singleStatus === 'pending' ? (
                        <>
                            <Button
                                variant='outlined'
                                color='secondary'
                                onClick={() => handleDeclineInvitation(singleInvitation.id)}
                                disabled={processingInvitationId === singleInvitation.id}
                            >
                                {_('Rechazar')}
                            </Button>
                            <Button
                                variant='contained'
                                color='secondary'
                                onClick={() => handleAcceptInvitation(singleInvitation.id)}
                                disabled={processingInvitationId === singleInvitation.id}
                            >
                                {_('Aceptar')}
                            </Button>
                        </>
                    ) : invitations.length > 1 && hasPendingInvitations ? (
                        isRV ? (
                            <Button
                                variant='text'
                                color='secondary'
                                onClick={handleAcceptAll}
                                disabled={acceptAllMutation.isMutating}
                            >
                                {_('Aceptar todas las solicitudes')}
                            </Button>
                        ) : (
                            <Stack direction='row' spacing={2} justifyContent='space-between' sx={{ width: '100%' }}>
                                <Button
                                    variant='text'
                                    color='secondary'
                                    onClick={handleDeclineAllAndNavigate}
                                    disabled={declineAllMutation.isMutating}
                                >
                                    {_('No trabajo con ninguno de estos RV')}
                                </Button>
                                <Button
                                    variant='contained'
                                    color='secondary'
                                    disabled={
                                        !selectedInvitation ||
                                        localStatusMap[selectedInvitation.id] !== 'pending' ||
                                        acceptInvitationMutation.isMutating
                                    }
                                    onClick={handleAcceptSelected}
                                >
                                    {_('Aceptar solicitud')}
                                </Button>
                            </Stack>
                        )
                    ) : null
                }
            >
                <Stack direction='column' spacing={2} sx={{ maxWidth: 600 }}>
                    {(() => {
                        if (singleInvitation) {
                            return (
                                <SingleInvitation
                                    invitation={singleInvitation}
                                    localStatus={singleStatus || 'pending'}
                                />
                            );
                        }
                        return (
                            <MultipleInvitations
                                invitations={invitations}
                                onInviteMoreAdvisors={onInviteMoreAdvisors}
                                selectedInvitation={selectedInvitation}
                                setSelectedInvitation={setSelectedInvitation}
                                localStatusMap={localStatusMap}
                                onAcceptInvitation={handleAcceptInvitation}
                                onDeclineInvitation={handleDeclineInvitation}
                                processingInvitationId={processingInvitationId}
                            />
                        );
                    })()}
                </Stack>
            </ModalV2>
        </AdvisorDistributorNamesProvider>
    );
};

export default RVOnboardingModalNewInvitationsReceived;
